import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, QueryList, ViewChildren } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';

@Component({
  selector: 'usb-request-header',
  templateUrl: './request-header.component.html',
  styleUrls: ['./request-header.component.scss']
})
export class UsbRequestHeaderComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() formGroup: FormGroup;
  @Input() selectedFile: any;
  @Input() selectedClient: any;
  @Input() availablePax: any[];
  @Input() loadingFiles: boolean;
  @Input() loadingPax: boolean;
  @Input() showNewFileNameInput: boolean;
  @Input() showNewClientInput: boolean;
  @Input() existingFiles: any[];
  @Input() countriesData: any[];
  @Input() loadingCountries: boolean;
  @Input() newFilePassenger: string;
  @Input() newFilePassengerError: boolean;
  @Input() newFilePhoneCode: string;
  @Input() newFilePhoneCodeError: boolean;
  @Input() newFilePhone: string;
  @Input() newFilePhoneError: boolean;
  @Input() newFileEmail: string;
  @Input() newFileEmailError: boolean;
  @Input() phoneCodeSelected: string;
  @Input() selectedFileId: string;
  @Input() selectedClientId: string;
  @Input() phoneCodeError: boolean;
  @Input() firstNameError: boolean;
  @Input() lastNameError: boolean;
  @Input() emailError: boolean;
  @Input() requiredFieldMessage: (tab: string, field: string) => string;
  @Input() getFileLabel: (f: any) => string;
  @Input() getClientLabel: (c: any) => string;
  @Input() getClientsForSelectedFile: () => any[];
  @Input() isNuevoFileValid: () => boolean;
  @Input() blockInvalidKeys: (event: KeyboardEvent, type: string) => void;
  @Input() displayCountryFn: (value: any) => string;
  @Input() edit: boolean = false;

  @Output() fileChange = new EventEmitter<any>();
  @Output() clientChange = new EventEmitter<any>();
  @Output() paxChange = new EventEmitter<any>();
  @Output() requestTypeChange = new EventEmitter<'quotation' | 'reservation' | 'on_hold'>();
  @Output() newFileToggle = new EventEmitter<void>();
  @Output() newClientToggle = new EventEmitter<void>();
  @Output() newFileSubmit = new EventEmitter<void>();
  @Output() newClientSubmit = new EventEmitter<{
    first_name: string;
    last_name: string;
    email: string;
    phone_code: string;
    phone: string;
  }>();
  @Output() fileSearchInput = new EventEmitter<string>();
  @Output() clearSelectedFile = new EventEmitter<void>();
  @Output() passengerIdChange = new EventEmitter<any>();
  @Output() passengerFirstNameChange = new EventEmitter<string>();
  @Output() passengerLastNameChange = new EventEmitter<string>();
  @Output() newFilePassengerInput = new EventEmitter<string>();
  @Output() newFilePhoneCodeInput = new EventEmitter<string>();
  @Output() newFileCountrySelected = new EventEmitter<any>();
  @Output() newFilePhoneInput = new EventEmitter<string>();
  @Output() newFileEmailInput = new EventEmitter<string>();
  @Output() countrySelected = new EventEmitter<any>();
  @Output() phoneCodeInput = new EventEmitter<string>();
  @Output() clientFirstNameChange = new EventEmitter<string>();
  @Output() clientLastNameChange = new EventEmitter<string>();
  @Output() clientEmailChange = new EventEmitter<string>();
  @Output() clientPhoneChange = new EventEmitter<string>();

  // ==========================================================
  // Tipo de solicitud (switch segmentado estilo Lovable)
  // ==========================================================

  /**
   * Opciones del switch de tipo de solicitud. `value` es el valor del
   * control `requestType` del FormGroup; `label` es lo que ve el usuario.
   */
  readonly requestTypeOptions: { value: string; label: string }[] = [
    { value: 'quotation', label: 'Cotización' },
    { value: 'reservation', label: 'Reserva' },
    { value: 'on_hold', label: 'Lista de espera' }
  ];

  /**
   * Botones del switch: se usan para medir la posición del "pill" activo
   * (mismo comportamiento que el Lovable, que mide offsetLeft/offsetWidth).
   */
  @ViewChildren('requestTypeOption') requestTypeOptionRefs!: QueryList<ElementRef<HTMLElement>>;

  /**
   * Posición y ancho del indicador deslizante. `null` hasta que se mide por
   * primera vez (el indicador no se pinta hasta entonces).
   */
  indicatorBox: { left: number; width: number } | null = null;

  /** Suscripción al `requestType` del FormGroup para reposicionar el pill. */
  private requestTypeSubscription?: Subscription;

  /** Listener de resize: el pill se recalcula al cambiar el ancho. */
  private readonly resizeListener = () => this.measureIndicator();

  ngOnInit(): void {
    const control = this.formGroup?.get('requestType');
    if (control) {
      this.requestTypeSubscription = control.valueChanges.subscribe(() => this.measureIndicator());
    }
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', this.resizeListener);
    }
  }

  ngAfterViewInit(): void {
    this.measureIndicator();
  }

  ngOnDestroy(): void {
    this.requestTypeSubscription?.unsubscribe();
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', this.resizeListener);
    }
  }

  /** Valor actual del tipo de solicitud. */
  get requestTypeValue(): string {
    return this.formGroup?.get('requestType')?.value ?? 'quotation';
  }

  /** true si la opción indicada es la activa. */
  isRequestTypeActive(value: string): boolean {
    return this.requestTypeValue === value;
  }

  /**
   * Mide el botón activo para poder dibujar el pill deslizante encima
   * (el mismo cálculo que hace el Lovable con offsetLeft/offsetWidth).
   */
  private measureIndicator(): void {
    const index = this.requestTypeOptions.findIndex(option => option.value === this.requestTypeValue);
    const refs = this.requestTypeOptionRefs?.toArray() ?? [];
    const element = index >= 0 ? refs[index]?.nativeElement : null;
    if (!element) {
      return;
    }
    this.indicatorBox = { left: element.offsetLeft, width: element.offsetWidth };
  }
}
